import {
  buildCashbookMovements,
  computeCashbookPeriodTotals,
  computeRunningBalance,
  sheetFooter,
  type CashbookEntryRecord,
  type CashbookSheetFooter,
  type CashbookSheetRow
} from '@finvox/shared/cashbook';
import type { TFunction } from 'i18next';
import { displayCashbookEntryLabel } from '@/lib/cashbookEntryLabel';

/** Case-insensitive match on description (category) and notes (memo). */
export function entryMatchesDescriptionQuery(
  entry: Pick<CashbookEntryRecord, 'categoryKey' | 'memo'>,
  query: string,
  t: TFunction,
  isArabic: boolean
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const key = entry.categoryKey.trim();
  if (key.toLowerCase().includes(q)) return true;
  const description = displayCashbookEntryLabel(key, t, isArabic);
  if (description.toLowerCase().includes(q)) return true;

  const memo = entry.memo?.trim() ?? '';
  if (!memo) return false;
  if (memo.toLowerCase().includes(q)) return true;
  const noteDisplay = displayCashbookEntryLabel(memo, t, isArabic);
  return noteDisplay.toLowerCase().includes(q);
}

export function filterCashEntriesByDescription(
  entries: CashbookEntryRecord[],
  options: {
    query: string;
    startDate: string;
    endDate: string;
    t: TFunction;
    isArabic: boolean;
  }
): CashbookEntryRecord[] {
  const q = options.query.trim();
  if (!q) return [];
  return entries.filter((e) => {
    if (e.status !== 'active') return false;
    if (e.date < options.startDate || e.date > options.endDate) return false;
    return entryMatchesDescriptionQuery(e, q, options.t, options.isArabic);
  });
}

export type DescriptionSearchSheet = {
  rows: CashbookSheetRow[];
  footer: CashbookSheetFooter;
  periodIn: string;
  periodOut: string;
  periodNet: string;
};

/** Entry-level sheet for search hits; running balance starts at 0 among matches. */
export function buildDescriptionSearchSheet(
  matchedEntries: CashbookEntryRecord[],
  range: { startDate: string; endDate: string }
): DescriptionSearchSheet {
  const usable = matchedEntries.filter(
    (e) =>
      e.status === 'active' && e.date >= range.startDate && e.date <= range.endDate
  );
  const movements = buildCashbookMovements(usable);
  const rows = computeRunningBalance('0.00', movements);
  const endBalance = rows.length > 0 ? rows[rows.length - 1]!.balance : '0.00';
  const footer = sheetFooter(rows, endBalance);
  const totals = computeCashbookPeriodTotals(movements, range);
  return {
    rows,
    footer,
    periodIn: totals.periodIn,
    periodOut: totals.periodOut,
    periodNet: totals.periodNet
  };
}
